import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { formatDate } from "../utils/date";
import { KHIDMAT_DETAILS, WHATSPP_LINK, KHIDMAT_DATES as khidmatDates } from "../core/constant";
import { getVolunteerStats } from "../core/api";

export default function Home() {

  const [liveStats, setLiveStats] = useState({
    totalRegistrations: '-',
    activeCoordinators: '35+',
    attendedCount: '-',
    daysRemaining: Math.ceil((new Date(khidmatDates.khidmatStartDate) - new Date()) / (1000 * 60 * 60 * 24)),
    eventEndsInDays: Math.ceil((new Date(khidmatDates.khidmatEndDate) - new Date()) / (1000 * 60 * 60 * 24))
  });

  // Photo gallery state for carousel
  const [photoGallery, setPhotoGallery] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Last year's stats
  const lastYearStats = {
    totalVolunteers: 1355,
    hoursServed: 1355 * 3,
    permanentlyJoined: 61,
    satisfactionRate: 98
  };

  // Dynamic photo loading using webpack's require.context
  useEffect(() => {
    const loadPhotos = () => {
      try {
        // Helper function to import all images from a directory
        function importAll(r) {
          let images = {};
          r.keys().map((item, index) => {
            images[item.replace('./', '')] = r(item);
            return null;
          });
          return images;
        }

        // Import all images from src/assets/gallery folder
        // This works with images in src folder, not public folder
        const images = importAll(
          require.context('/public/images/gallery', false, /\.(png|jpe?g|gif|svg)$/)
        );

        // Convert to array of image URLs
        const photoUrls = Object.entries(images).map(([path, module]) => ({
          src: module.default || module,
          name: path
        }));

        setPhotoGallery(photoUrls);
        console.log('Loaded photos:', photoUrls.length);
      } catch (error) {
        console.warn('Could not load gallery photos from src/assets/gallery:', error);

        // Fallback to static images if require.context fails
        const fallbackPhotos = [
          { src: "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&h=400&fit=crop", name: "community-1.jpg" },
          { src: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=600&h=400&fit=crop", name: "food-1.jpg" },
          { src: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600&h=400&fit=crop", name: "healthcare-1.jpg" },
          { src: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=600&h=400&fit=crop", name: "traffic-1.jpg" },
          { src: "https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=600&h=400&fit=crop", name: "volunteers-1.jpg" },
          { src: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=600&h=400&fit=crop", name: "service-1.jpg" }
        ];

        setPhotoGallery(fallbackPhotos);
      }
    };

    loadPhotos();
  }, []);

  const fetchLiveStats = async () => {
    const stats = await getVolunteerStats();
    setLiveStats(prev => ({ ...prev, ...stats }));
  };
  useEffect(() => {
    fetchLiveStats();
  }, []);

  // Carousel navigation functions
  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photoGallery.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photoGallery.length) % photoGallery.length);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Hero Section with Live Stats */}
      <section className="relative bg-gradient-to-br from-gray-50 via-white to-amber-50 py-20 overflow-x-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(255,193,7,0.2) 2px, transparent 0), radial-gradient(circle at 75px 75px, rgba(255,193,7,0.1) 2px, transparent 0)', backgroundSize: '100px 100px' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-gray-900 font-serif leading-tight">
              <span className="block sm:inline">Shehrullah</span>{" "}
              <span className="block sm:inline">Khidmat Sharaf</span>{" "}
              <br className="hidden sm:block" />
              <span className="text-amber-600 block sm:inline">Dubai 1447H</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-600 font-light">
              Serving the Community During the Holy Month of Ramadan
            </p>


            {/* Intro Video */}
            <div className="max-w-4xl mx-auto">
              <div className="relative aspect-video bg-gray-100 rounded-2xl overflow-hidden shadow-lg">
                <video
                  className="w-full h-full object-cover"
                  controls
                  controlsList="nodownload"
                  preload="metadata"
                  onError={(e) => {
                    console.error('Video failed to load:', e);
                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full bg-gray-100 text-gray-500"><div class="text-center"><div class="text-4xl mb-2">📹</div><p>Video not available</p></div></div>';
                  }}
                >
                  <source src="/Shehrullah_Sharaf_Intro_Video.mp4" type="video/mp4" />
                  <source src="/Shehrullah_Sharaf_Intro_Video.webm" type="video/webm" />
                  <source src="/Shehrullah_Sharaf_Intro_Video.mov" type="video/quicktime" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>

          {/* Registration and Important Dates Section */}
          <section className="bg-gradient-to-br from-amber-25 to-yellow-50 py-8 sm:py-16 rounded-2xl shadow-lg mb-16 sm:mb-24 border-2 border-yellow-600">
            <div className="max-w-6xl mx-auto px-2 sm:px-2">
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-amber-900">
                  Register Now for Shehrullah Khidmat 1447H
                </h2>
                <p className="text-base sm:text-lg text-amber-700 px-2">
                  Join thousands of volunteers making a difference in our community
                </p>
                <p className="text-sm sm:text-base text-amber-600 mt-2 font-medium">
                  For Gents and Kids 11 years and above
                </p>
              </div>

              {/* Desktop: 2 columns, Mobile: rows */}
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-4">

                {/* Left Column: Registration */}
                <div className="flex-1 bg-white rounded-3xl shadow-xl border border-gray-100 p-6 lg:p-8">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Calendar Image */}
                    <div className="flex justify-center lg:flex-1">
                      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-2xl border border-amber-200 shadow-lg">
                        <img
                          src="/shehrullah_calendar_1447_new.jpeg"
                          alt="Shehrullah Khidmat Calendar 1447H"
                          className="w-full max-w-xs h-auto rounded-xl shadow-md"
                          onError={(e) => {
                            if (e.target.src.includes('.jpg')) {
                              e.target.src = '/shehrullah_calendar_1447_2.png';
                            } else if (e.target.src.includes('.png')) {
                              e.target.src = '/shehrullah_calendar_1447_2.jpg';
                            }
                          }}
                        />
                        <p className="text-center text-amber-700 font-medium mt-3 text-sm">Hijri Calendar 1447H</p>
                      </div>
                    </div>

                    {/* Date Information */}
                    <div className="flex-1 space-y-4  hidden lg:grid">
                      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl border border-amber-200 hover:shadow-lg transition-all duration-300">
                        <h4 className="font-bold mb-2 text-gray-900 text-sm lg:text-base">Registration Start</h4>
                        <p className="text-amber-600 font-semibold text-xs lg:text-sm">
                          {formatDate(khidmatDates.registrationStart)}<br />
                          {khidmatDates.registrationStartInHijri}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl border border-amber-200 hover:shadow-lg transition-all duration-300">
                        <h4 className="font-bold mb-2 text-gray-900 text-sm lg:text-base">Registration End</h4>
                        <p className="text-amber-600 font-semibold text-xs lg:text-sm">
                          {formatDate(khidmatDates.registrationEnd)}<br />
                          {khidmatDates.registrationEndInHijri}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl border border-amber-200 hover:shadow-lg transition-all duration-300">
                        <h4 className="font-bold mb-2 text-gray-900 text-sm lg:text-base">Khidmat Period</h4>
                        <p className="text-amber-600 font-semibold text-xs lg:text-sm">
                          {formatDate(khidmatDates.khidmatStartDate)} ({khidmatDates.khidmatStartInHijri})<br />
                          to {formatDate(khidmatDates.khidmatEndDate)} ({khidmatDates.khidmatEndInHijri})
                        </p>
                      </div>
                    </div>
                  </div>
                </div>


                {/* Right Column: Important Dates */}
                <div className="flex-1 bg-white rounded-3xl p-6 lg:p-8 shadow-xl border border-gray-100">

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-6 lg:gap-8">
                    {/* QR Code */}
                    <div className="w-full max-w-xs mx-auto sm:mx-0 sm:w-auto">
                      <div className="bg-gradient-to-br from-amber-50 to-yellow-100 p-6 sm:p-8 rounded-xl shadow-md text-center border-2 border-yellow-600">
                        <h3 className="text-base sm:text-lg font-semibold mb-4 text-amber-900">
                          Scan to Register via WhatsApp
                        </h3>
                        <div className="flex justify-center">
                          <QRCodeCanvas
                            value={WHATSPP_LINK}
                            size={160}
                            bgColor="#ffffff"
                            fgColor="#000000"
                            level="M"
                            className="max-w-full h-auto"
                          />
                        </div>
                        <p className="text-amber-700 mt-4 text-sm">Quick Registration via WhatsApp</p>
                      </div>
                    </div>

                    {/* Contact Info & CTA */}
                    <div className="w-full max-w-sm mx-auto sm:mx-0 sm:w-auto text-center sm:text-left">
                      <div className="mb-6">
                        <a
                          href={WHATSPP_LINK}
                          className="inline-block w-full sm:w-auto px-6 sm:px-4 py-3 sm:py-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-white text-lg sm:text-xl font-semibold rounded-xl hover:from-amber-700 hover:to-yellow-700 transition-all duration-300 transform hover:scale-105 shadow-lg border border-yellow-500"
                        >
                          📱Click Here to Register Now
                        </a>
                      </div>

                      <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-4 sm:p-2 rounded-xl border-2 border-yellow-600">
                        <p className="text-amber-800 mb-2 text-sm sm:text-base">For more details, contact us at:</p>
                        <p className="font-semibold text-base sm:text-lg text-amber-900">📞 +971 555576879</p>
                        <p className="text-xs sm:text-sm text-gray-600 mt-4">
                          Registration closes on {formatDate(khidmatDates.registrationEnd)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>


          {/* Live Registration Stats */}
          <section className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-serif mb-4">
                Live Registration Stats
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Real-time updates on our community's participation
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl text-center shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <h3 className="text-2xl md:text-3xl font-bold text-amber-600 mb-2">{liveStats.totalRegistrations}</h3>
                <p className="text-sm md:text-base text-gray-600 font-medium">Total Registrations</p>
              </div>
              <div className="bg-white p-6 rounded-2xl text-center shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <h3 className="text-2xl md:text-3xl font-bold text-amber-600 mb-2">{liveStats.activeCoordinators}</h3>
                <p className="text-sm md:text-base text-gray-600 font-medium">Active Coordinators</p>
              </div>
              <div className="bg-white p-6 rounded-2xl text-center shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <h3 className="text-2xl md:text-3xl font-bold text-amber-600 mb-2">{liveStats.attendedCount}</h3>
                <p className="text-sm md:text-base text-gray-600 font-medium">Completed Khidmat</p>
              </div>
              <div className="bg-white p-6 rounded-2xl text-center shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <h3 className="text-2xl md:text-3xl font-bold text-amber-600 mb-2">{liveStats.eventEndsInDays}</h3>
                <p className="text-sm md:text-base text-gray-600 font-medium">Days Remaining</p>
              </div>
            </div>
          </section>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Khidmat Services Cards */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-serif mb-4">
              Khidmats
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover the various ways you can contribute to our community
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {KHIDMAT_DETAILS.map((service) => (
              <Link
                key={service.id}
                to={`/khidmat/${service.key.toLowerCase()}`}
                className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 group border border-gray-100 flex flex-col h-full block"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
                <div className="p-8 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold mb-3 text-gray-900 font-serif">{service.title}</h3>
                  {service.subTitle && (
                    <h4 className="text-md font-semibold mb-2 text-amber-700">{service.subTitle}</h4>
                  )}
                  <p className="text-gray-600 mb-6 text-sm leading-relaxed flex-grow">{service.description}</p>
                  <div className="mt-auto">
                    <div className="inline-flex items-center px-6 py-3 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all duration-300 text-sm font-medium shadow-lg hover:shadow-xl group-hover:scale-105">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Learn More
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Last Year Stats */}
        <section className="bg-gradient-to-r from-amber-600 via-amber-700 to-amber-600 text-white py-8 sm:py-16 rounded-2xl shadow-lg mb-8 sm:mb-16 border-t-4 border-yellow-500">
          <div className="px-3 sm:px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Shehrullah Khidmat 1446 Impact
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8">
              <div className="text-center">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm p-6 rounded-xl">
                  <h3 className="text-3xl md:text-4xl font-bold mb-2">{lastYearStats.totalVolunteers.toLocaleString()}</h3>
                  <p className="text-sm md:text-base">Total Volunteers Registered</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm p-6 rounded-xl">
                  <h3 className="text-3xl md:text-4xl font-bold mb-2">{lastYearStats.hoursServed.toLocaleString()}</h3>
                  <p className="text-sm md:text-base">Hours Served</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm p-6 rounded-xl">
                  <h3 className="text-3xl md:text-4xl font-bold mb-2">{lastYearStats.permanentlyJoined.toLocaleString()}</h3>
                  <p className="text-sm md:text-base">Joined Khidmat
                    Permanently</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm p-6 rounded-xl">
                  <h3 className="text-3xl md:text-4xl font-bold mb-2">{lastYearStats.satisfactionRate}%</h3>
                  <p className="text-sm md:text-base">Satisfaction Rate</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Photo Gallery with Carousel */}
        <section className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Memories from Shehrullah Khidmat Sharaf 1446
          </h2>

          {photoGallery.length > 0 && (
            <>
              {/* Main Carousel */}
              <div className="relative mb-8">
                <div className="relative h-64 sm:h-96 md:h-[500px] bg-gray-200 rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src={photoGallery[currentPhotoIndex]?.src}
                    alt={photoGallery[currentPhotoIndex]?.name || `Gallery ${currentPhotoIndex + 1}`}
                    className="w-full h-full object-contain transition-opacity duration-500"
                  />

                  {/* Navigation Arrows */}
                  <button
                    onClick={prevPhoto}
                    className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-amber-700/80 hover:bg-amber-800/90 text-white p-2 sm:p-3 rounded-full transition-all duration-200 border border-yellow-500">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <button
                    onClick={nextPhoto}
                    className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-amber-700/80 hover:bg-amber-800/90 text-white p-2 sm:p-3 rounded-full transition-all duration-200 border border-yellow-500">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Photo Counter */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-amber-700/80 text-white px-4 py-2 rounded-full text-sm border border-yellow-500">
                    {currentPhotoIndex + 1} / {photoGallery.length}
                  </div>
                </div>
              </div>

              {/* Thumbnail Grid */}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-10 gap-1 sm:gap-2 overflow-x-auto">
                {photoGallery.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPhotoIndex(index)}
                    className={`aspect-square overflow-hidden rounded-lg transition-all duration-200 ${index === currentPhotoIndex
                      ? 'ring-4 ring-yellow-600 scale-105'
                      : 'hover:scale-105 opacity-70 hover:opacity-100'
                      }`}
                  >
                    <img
                      src={photo.src}
                      alt={photo.name || `Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Fallback if no photos */}
          {photoGallery.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📸</div>
              <p className="text-gray-600">Loading gallery photos...</p>
            </div>
          )}
        </section>
      </div >
    </div >
  );
}